import { ILanguageModel, INotificationTemplateDto } from "@kuika/kuika-cl-model";
import * as React from "react";
import KuikaButton, { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaEntitySwitcher, {
  KuikaEntitySwitcherGroup
} from "../../ui-kit/kuika-entity-switcher/kuika-entity-switcher";
import { KuikaListTableRowAction } from "../../ui-kit/kuika-list-table/kuika-list-table";
import KuikaPageHeader from "../../ui-kit/kuika-page-header/kuika-page-header";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { isSystemEmailTemplate } from "./email-template-row-actions";
import { IEmailLanguageOption } from "./email-templates";
import NotificationTemplateLanguageControl from "./notification-template-language-control";
import { NotificationTemplateTitle } from "./notification-template-title";

/**
 * The editor's top bar: the template name (which is also the rename control and the
 * template switcher), the multi-language controls, and UPDATE.
 *
 * Presentation only — every handler belongs to the canvas, which owns the editor state
 * and the Unlayer instance. The back control is the only way out, and it runs the same
 * guarded close path CANCEL used to.
 */

/** Yours or the platform's — the split the list's Type column already makes. */
const EMAIL_TEMPLATE_GROUPS: KuikaEntitySwitcherGroup[] = [
  { key: "custom", label: "Custom" },
  { key: "system", label: "System" }
];

export interface INotificationTemplateEditorHeaderProps {
  templateName: string;
  isNameOnEdit: boolean;
  canRename: boolean;
  onStartRename: () => void;
  onSubmitName: (name: string) => void;
  onAbortRename: () => void;
  nameValidator: (rule: unknown, value: string) => Promise<void>;
  validateName: () => Promise<string | undefined>;

  templates: INotificationTemplateDto[];
  currentTemplate: INotificationTemplateDto | undefined;
  onSwitchTemplate: (template: INotificationTemplateDto) => void;
  onCreateNew: () => void;

  isMlEnabled: boolean;
  onMlEnabledChange: (checked: boolean) => void;
  languages: ILanguageModel[];
  currentLanguage: ILanguageModel | undefined;
  onChangeLanguage: (shortName: string) => void;
  addableLanguages: IEmailLanguageOption[];
  onAddLanguage: (language: IEmailLanguageOption) => void;

  /** Leaving the editor. The bar's back control draws the only control for it. */
  onBack: () => void;
  /** The template list's own row actions, for the switcher's "…". */
  rowActions: KuikaListTableRowAction<INotificationTemplateDto>[];
  onUpdate: () => void;
  /**
   * Where UPDATE stands (`KuikaButton` `status`): "Saving", then "Saved" or "Try again". A save
   * closes the editor, so the canvas holds the ✓ here before it leaves for the list.
   */
  updateStatus: KuikaButtonStatus;
}

export class NotificationTemplateEditorHeader extends React.PureComponent<INotificationTemplateEditorHeaderProps> {
  renderTitle = (): React.ReactNode => (
    <NotificationTemplateTitle
      templateName={this.props.templateName}
      isEditing={this.props.isNameOnEdit}
      canRename={this.props.canRename}
      onStartEdit={this.props.onStartRename}
      onSubmit={this.props.onSubmitName}
      onAbort={this.props.onAbortRename}
      nameValidator={this.props.nameValidator}
      validateName={this.props.validateName}
      identity={
        <KuikaEntitySwitcher<INotificationTemplateDto>
          trigger="chip"
          triggerIcon={<KuikaSvg name="email-builder" type="topbar-icons" width={16} height={16} />}
          triggerLabel={this.props.templateName}
          triggerLabelTestId="emailBuilderTitle"
          items={this.props.templates}
          current={this.props.currentTemplate}
          onSelect={this.props.onSwitchTemplate}
          onCreateNew={this.props.onCreateNew}
          entityLabel="Email Template"
          iconName="mail"
          triggerTestId="emailTemplateSwitcherTrigger"
          itemTestId={(template: INotificationTemplateDto) => `${template.name}SwitcherItem`}
          groups={EMAIL_TEMPLATE_GROUPS}
          getItemGroup={(template: INotificationTemplateDto): string =>
            isSystemEmailTemplate(template) ? "system" : "custom"
          }
          rowActions={this.props.rowActions}
          onManageAll={this.props.onBack}
        />
      }
    />
  );

  render() {
    return (
      <KuikaPageHeader
        variant="bar"
        title={this.props.templateName || "Email Builder"}
        onBack={this.props.onBack}
        backLabel="Back to Email Builder"
        backTestId="emailBuilderBackButton"
        renderTitle={this.renderTitle}
        actions={
          <>
            <NotificationTemplateLanguageControl
              isMlEnabled={this.props.isMlEnabled}
              languages={this.props.languages}
              currentLanguage={this.props.currentLanguage}
              addableLanguages={this.props.addableLanguages}
              onChangeLanguage={this.props.onChangeLanguage}
              onAddLanguage={this.props.onAddLanguage}
              onDisableMultiLanguage={() => this.props.onMlEnabledChange(false)}
            />
            <KuikaButton
              data-testid="updateButton"
              type="primary"
              size="md"
              status={this.props.updateStatus}
              onClick={this.props.onUpdate}
            >
              UPDATE
            </KuikaButton>
          </>
        }
      />
    );
  }
}
